import React, { useState } from 'react';
import './HeroRecommendationCard.css';
import { Button } from '../Button';
import { StarIcon, EyeIcon, ShareIcon, CompareIcon } from '../Button/icons';
import { ProductDetailsSlideout } from '../ProductDetailsSlideout';

export interface HeroRecommendationCardProps {
  /** Product name */
  productName: string;
  /** Product description */
  description: string;
  /** URL for the product image */
  imageUrl?: string;
  /** Alt text for the product image */
  imageAlt?: string;
  /** Array of feature tags/badges */
  tags?: string[];
  /** Match percentage (0-100) */
  matchPercentage?: number;
  /** Feature description text */
  featureDescription?: string;

  /** Handler for VIEW DETAILS button click */
  onViewDetails?: () => void;
  /** Handler for Compare button toggle */
  onCompareChange?: (checked: boolean) => void;
  /** Whether the item is currently being compared */
  isCompared?: boolean;
  /** Handler for Share button click */
  onShare?: () => void;
  /** Whether this is marked as top recommendation */
  isTopRecommendation?: boolean;
  /** Optional CSS class name */
  className?: string;
}

export const HeroRecommendationCard: React.FC<HeroRecommendationCardProps> = ({
  productName,
  description,
  imageUrl,
  imageAlt = productName,
  tags = [],
  matchPercentage,
  featureDescription,

  onViewDetails,
  onCompareChange,
  isCompared = false,
  onShare,
  isTopRecommendation = false,
  className = '',
}) => {
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);

  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullFeatureDescription, setShowFullFeatureDescription] = useState(false);

  const cardClasses = [
    'hero-recommendation-card',
    className
  ].filter(Boolean).join(' ');



  const handleViewDetails = () => {
    setIsSlideoutOpen(true);
    onViewDetails?.();
  };

  const handleCloseSlideout = () => {
    setIsSlideoutOpen(false);
  };

  return (
    <div className={cardClasses}>
      {isTopRecommendation && (
        <div className="hero-recommendation-card__top-banner">
          <StarIcon className="hero-recommendation-card__star-icon" />
          <span className="hero-recommendation-card__top-text">Top Recommendation</span>
        </div>
      )}

      <div className="hero-recommendation-card__header">
        <div className="hero-recommendation-card__title-section">
          <h3 className="hero-recommendation-card__product-name">{productName}</h3>
          <div className="hero-recommendation-card__description-container">
            <p className={`hero-recommendation-card__description ${showFullDescription ? 'hero-recommendation-card__description--expanded' : ''}`}>
              {description}
            </p>
            {description && description.length > 250 && (
              <button
                className="hero-recommendation-card__description-toggle"
                onClick={() => setShowFullDescription(!showFullDescription)}
              >
                {showFullDescription ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>

        <div className="hero-recommendation-card__compare-section">
          <button
            className={`hero-recommendation-card__compare-button ${isCompared ? 'hero-recommendation-card__compare-button--active' : ''}`}
            onClick={() => onCompareChange?.(!isCompared)}
          >
            {isCompared ? (
              <>✓ Added</>
            ) : (
              <>
                <CompareIcon className="hero-recommendation-card__compare-icon" />
                Compare
              </>
            )}
          </button>
        </div>
      </div>

      <div className="hero-recommendation-card__content">
        {imageUrl && (
          <div className="hero-recommendation-card__image-section">
            <img
              src={imageUrl}
              alt={imageAlt}
              className="hero-recommendation-card__image"
            />
          </div>
        )}

        <div className="hero-recommendation-card__details">
          {tags.length > 0 && (
            <div className="hero-recommendation-card__tags">
              {tags.map((tag, index) => (
                <span key={index} className="hero-recommendation-card__tag">
                  {tag}
                </span>
              ))}
            </div>
          )}



          {featureDescription && (
            <div className="hero-recommendation-card__feature-description-container">
              <p className={`hero-recommendation-card__feature-description ${showFullFeatureDescription ? 'hero-recommendation-card__feature-description--expanded' : ''}`}>
                {featureDescription}
              </p>
              {featureDescription.length > 300 && (
                <button
                  className="hero-recommendation-card__feature-description-toggle"
                  onClick={() => setShowFullFeatureDescription(!showFullFeatureDescription)}
                >
                  {showFullFeatureDescription ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}

          <div className="hero-recommendation-card__actions">
            <Button
              icon={<ShareIcon />}
              isIconOnly
              onClick={onShare}
              size="medium"
              type="outline"
            >
              Share
            </Button>
            <Button
              type="primary"
              size="medium"
              icon={<EyeIcon />}
              iconPosition="left"
              onClick={handleViewDetails}
            >
              VIEW DETAILS
            </Button>
          </div>
        </div>
      </div>

      {/* Product Details Slideout */}
      <ProductDetailsSlideout
        isOpen={isSlideoutOpen}
        onClose={handleCloseSlideout}
        productName={productName}
        description={description}
        heroImageUrl={imageUrl}
        imageAlt={imageAlt}
        matchPercentage={matchPercentage}
        matchDescription={featureDescription}
        isTopRecommendation={isTopRecommendation}
        overview={`The ${productName} provides a complete video conference room solution for open-platform UC applications. ${featureDescription || ''}`}
        onTalkToSales={() => console.log('Talk to Sales clicked')}
        onAskAI={() => console.log('Ask AI clicked')}
        onShare={() => console.log('Share clicked')}
      />
    </div>
  );
};
